import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { QRCodeModal } from './QRCodeModal';

interface SpaceLinkBannerProps {
  spaceUrl: string;
}

export function SpaceLinkBanner({ spaceUrl }: SpaceLinkBannerProps) {
  const [copied, setCopied] = useState(false);
  const [isVisible, setIsVisible] = useState(true);
  const [showQRCode, setShowQRCode] = useState(false);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(spaceUrl);
      // Track copy link event
      if (window.umami) {
        window.umami.track('copy-space-link');
      }
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  return (
    <>
      <AnimatePresence>
        {isVisible && (
          <motion.div
            key="space-link-banner"
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: 20 }}
            className="fixed bottom-4 right-4 md:bottom-auto md:top-1/2 md:-translate-y-1/2 md:right-6 z-40 w-[calc(100%-2rem)] md:w-80 max-w-md"
          >
          <div className="bg-gradient-to-br from-blue-500/10 to-purple-500/10 backdrop-blur-md border border-white/10 rounded-lg p-4 shadow-2xl relative">
            <button
              onClick={() => setIsVisible(false)}
              className="absolute top-3 right-3 p-1 rounded-md hover:bg-white/10 transition-colors group"
              aria-label="Close"
            >
              <svg className="w-4 h-4 text-gray-400 group-hover:text-white transition-colors" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
            <div className="flex flex-col gap-3">
              <div className="flex items-center gap-2 pr-6">
                <div className="flex-shrink-0 w-8 h-8 rounded-lg bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center">
                  <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" />
                  </svg>
                </div>
                <h3 className="text-sm font-semibold text-white">Share this space</h3>
              </div>
          <div className="space-y-2">
            <p className="text-xs text-gray-400">Copy this link to invite others</p>
            <div className="flex items-center gap-2 bg-black/20 rounded-lg p-2 border border-white/5">
              <input
                type="text"
                value={spaceUrl}
                readOnly
                className="flex-1 bg-transparent text-xs text-gray-300 font-mono truncate outline-none"
              />
            </div>
            <div className="grid grid-cols-2 gap-2">
              <button
                onClick={handleCopy}
                className="px-4 py-2.5 bg-gradient-to-r from-blue-500 to-purple-600 hover:from-blue-600 hover:to-purple-700 rounded-lg text-white text-sm font-medium transition-all shadow-lg hover:shadow-xl active:scale-95"
              >
                {copied ? (
                  <span className="flex items-center justify-center gap-2">
                    <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                    Copied!
                  </span>
                ) : (
                  <span className="flex items-center justify-center gap-2">
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                    </svg>
                    Copy
                  </span>
                )}
              </button>
              
              <button
                onClick={() => setShowQRCode(true)}
                className="px-4 py-2.5 bg-white/5 hover:bg-white/10 border border-white/10 rounded-lg text-white text-sm font-medium transition-all active:scale-95"
              >
                <span className="flex items-center justify-center gap-2">
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v1m6 11h2m-6 0h-2v4m0-11v3m0 0h.01M12 12h4.01M16 20h4M4 12h4m12 0h.01M5 8h2a1 1 0 001-1V5a1 1 0 00-1-1H5a1 1 0 00-1 1v2a1 1 0 001 1zm12 0h2a1 1 0 001-1V5a1 1 0 00-1-1h-2a1 1 0 00-1 1v2a1 1 0 001 1zM5 20h2a1 1 0 001-1v-2a1 1 0 00-1-1H5a1 1 0 00-1 1v2a1 1 0 001 1z" />
                  </svg>
                  QR Code
                </span>
              </button>
            </div>
            </div>
          </div>
          </div>
        </motion.div>
        )}
      </AnimatePresence>
      
      {/* QR Code Modal */}
      <QRCodeModal
        isOpen={showQRCode}
        onClose={() => setShowQRCode(false)}
        spaceUrl={spaceUrl}
      />
    </>
  );
}
